import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts'
import Loader from 'components/loader'
import { currencyFormat, formateDate } from 'utils'
import { analyticsApi } from '../restaurant.api'

function StatTile({ label, value }) {
  return (
    <div className='tw-rounded-[8px] tw-border tw-border-[#E7DFD9] tw-bg-white tw-p-4'>
      <p className='tw-text-xs tw-text-gray-500'>{label}</p>
      <p className='tw-mt-1 tw-text-2xl tw-font-bold tw-text-navyBlue'>{value}</p>
    </div>
  )
}

function Analytics() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState(null)
  const [monthlySales, setMonthlySales] = useState([])

  useEffect(() => {
    ;(async () => {
      const [dashboardRes, reportsRes] = await Promise.all([
        analyticsApi.dashboard(),
        analyticsApi.reports(),
      ])
      if (dashboardRes.response) setStats(dashboardRes.response.data || null)
      if (reportsRes.response)
        setMonthlySales(reportsRes.response.data?.monthlySales || [])
      setLoading(false)
    })()
  }, [])

  if (loading) return <Loader />

  const recentOrders = stats?.recentOrders || []

  return (
    <div>
      <div className='tw-mb-4'>
        <button
          onClick={() => navigate('/order-management')}
          className='tw-text-[12px] tw-text-gray-400'
        >
          ← Order management
        </button>
        <h2 className='font-heading tw-text-2xl'>Analytics</h2>
      </div>

      <div className='tw-grid tw-gap-4 tw-grid-cols-2 lg:tw-grid-cols-4 tw-mb-6'>
        <StatTile label='Total orders' value={stats?.totalOrders ?? 0} />
        <StatTile
          label='Total revenue'
          value={currencyFormat(stats?.totalRevenue || 0)}
        />
        <StatTile label='Products' value={stats?.totalProducts ?? 0} />
        <StatTile label='Customers' value={stats?.totalCustomers ?? 0} />
      </div>

      <div className='tw-rounded-[8px] tw-border tw-border-[#E7DFD9] tw-bg-white tw-p-4 tw-mb-6'>
        <h3 className='tw-font-semibold tw-mb-3'>Monthly sales</h3>
        {monthlySales.length && monthlySales.reduce((sum, item) => sum + (item.sales || 0), 0) > 0 &&
        (stats?.totalRevenue || 0) > 0 ? (
          <ResponsiveContainer width='100%' height={280}>
            <BarChart data={monthlySales}>
              <CartesianGrid strokeDasharray='3 3' stroke='#eee' />
              <XAxis dataKey='name' fontSize={12} />
              <YAxis fontSize={12} />
              <Tooltip formatter={(value) => currencyFormat(value)} />
              <Bar dataKey='sales' fill='#4A403A' radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className='tw-text-sm tw-text-gray-400 tw-py-8 tw-text-center'>
            No sales data yet
          </p>
        )}
      </div>

      <div className='tw-rounded-[8px] tw-border tw-border-[#E7DFD9] tw-bg-white tw-p-4'>
        <h3 className='tw-font-semibold tw-mb-3'>Recent orders</h3>
        {recentOrders.length ? (
          <div className='tw-flex tw-flex-col tw-gap-2'>
            {recentOrders.map((order) => (
              <div
                key={order._id}
                className='tw-flex tw-items-center tw-justify-between tw-text-sm tw-border-b tw-border-[#F0F0F0] tw-pb-2'
              >
                <div>
                  <span className='tw-font-mono tw-text-[12px] tw-text-navyBlue'>
                    {order.orderNumber}
                  </span>
                  <span className='tw-text-gray-400 tw-ml-2 tw-text-[11px]'>
                    {order.createdAt ? formateDate(order.createdAt) : ''}
                  </span>
                </div>
                <div className='tw-flex tw-items-center tw-gap-3'>
                  <span className='tw-text-gray-500 tw-text-[12px]'>
                    {order.status}
                  </span>
                  <span className='tw-font-semibold'>
                    {currencyFormat(order.totalAmount || 0)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className='tw-text-sm tw-text-gray-400 tw-py-4 tw-text-center'>
            No recent orders
          </p>
        )}
      </div>
    </div>
  )
}

export default Analytics
